import { useState, useEffect } from "react";
import { kai_get_user_role, send_kai_message } from "../../../constants/apis";
import { useAppContext } from "../../../context/AppContext";

import ChatHeader from "./ChatHeader";
import { ChatMessages } from "./ChatMessages";
import ChatInput from "./ChatInput";
import { AiIcon } from "../../Icons";
import useStickyState from "../../../hooks/useStickyState";
import Drawer from "../../Shared/Drawer";

export type ChatMessage = {
  content: string;
  sender: "user" | "ai";
  time: string;
};

type KAIUserRole = {
  mobile: string;
  role: string;
};

export default function KaiChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useStickyState<ChatMessage[]>([], "kai_chat");
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [kaiUserRole, setKaiUserRole] = useStickyState<KAIUserRole | null>(
    null,
    "kai_user_role"
  );

  const { user, userLoading } = useAppContext();

  useEffect(() => {
    if (kaiUserRole) return;

    if (user && isOpen) startChat(user.work_email);
  }, [user, isOpen, kaiUserRole]);

  useEffect(() => {
    const body = document.body;
    const scrollbarWidth = window.innerWidth - body.clientWidth;

    if (isOpen) {
      body.style.overflow = "hidden";
      body.style.paddingRight = `${scrollbarWidth}px`; // prevent layout shift
    } else {
      body.style.overflow = "";
      body.style.paddingRight = "";
    }

    return () => {
      body.style.overflow = "";
      body.style.paddingRight = "";
    };
  }, [isOpen]);

  const startChat = async (email: string) => {
    try {
      const res = await fetch(`${kai_get_user_role}?mobile=${email}`);
      if (!res.ok) throw new Error("Fail to fetch");

      const user = await res.json();

      setKaiUserRole(user);

      addMessage(
        "👋 Hi there! I'm KAI, your logistics assistant. How can I help you today?",
        "ai"
      );
    } catch (err) {
      console.error("Error starting chat:", err);
      addMessage("⚠️ Failed to start chat. Please try again.", "ai");
    }
  };

  const addMessage = (
    content: string,
    sender: ChatMessage["sender"] = "ai"
  ) => {
    const msg = {
      content,
      sender,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    setMessages((prev) => [...prev, msg]);
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    const userMessage = input.trim();
    setInput("");
    addMessage(userMessage, "user");
    setTyping(true);

    try {
      const res = await fetch(send_kai_message, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile: user?.work_email,
          message: userMessage,
          session_id: localStorage.getItem("kai_session_id"),
          order_id: localStorage.getItem("kai_order_id"),
        }),
      });

      const data = await res.json();

      if (data.session_id)
        localStorage.setItem("kai_session_id", data.session_id);
      if (data.order_id) localStorage.setItem("kai_order_id", data.order_id);

      addMessage(data.response, "ai");
    } catch (err) {
      console.error("Chat error:", err);
      addMessage("⚠️ Sorry, I couldn’t connect. Please try again later.", "ai");
    } finally {
      setTyping(false);
    }
  };

  // const handleLogout = () => {
  //   localStorage.removeItem("kai_email");
  //   localStorage.removeItem("kai_session_id");
  //   localStorage.removeItem("kai_order_id");
  //   setMessages([]);
  // };

  return (
    <>
      {/* KAI Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="bg-custom-blue-medium text-sm py-2 px-4 flex items-center gap-2 text-white font-medium rounded-2xl disabled:bg-custom-blue-medium/40 disabled:cursor-not-allowed"
        disabled={userLoading}
      >
        <AiIcon className="size-6" /> Talk to KAI
      </button>

      {/* BACKDROP */}
      {/* {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        ></div>
      )} */}

      {/* Chat Window */}

      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <ChatHeader onClose={() => setIsOpen(false)} />

          <ChatMessages messages={messages} typing={typing} />

          <ChatInput input={input} setInput={setInput} onSend={sendMessage} />
        </div>

        {/* Footer Actions for Role */}
        {/* {kaiUserRole?.role && (
          <div className="border-t p-3 flex justify-between bg-gray-50 text-sm">
            <button
              onClick={() => addMessage("Show my open orders", "user")}
              className="text-blue-600 hover:underline"
            >
              View Orders
            </button>
            <button
              onClick={handleLogout}
              className="text-red-500 hover:underline"
            >
              End Chat
            </button>
          </div>
        )} */}
      </Drawer>
    </>
  );
}
